import { FC, useEffect, useState } from 'react';
import { FieldControl, SelectBox } from 'src/UI';
import { RelationFieldType } from '.';
import { IBaseField } from '..';
import API from './API';
import type { Select2WebElementModel } from './API';

interface IRelationSelectBoxProps extends IBaseField {
	allowAdd?: boolean;
	relation: RelationFieldType;
	pageCount?: number;
	onChangeValue: (value: any) => void;
	setTouched: (value: boolean, shouldValidate?: boolean) => void;
}

const RelationSelectBox: FC<IRelationSelectBoxProps> = (props) => {
	const {
		name,
		title,
		relation,
		pageCount = 20,
		isRequired = false,
		multiple,
		allowAdd,
		disabled = false,
		placeholder,
		value,
		setTouched,
		onChangeValue,
		onBlur,
		touched = false,
		error,
	} = props;

	const valueField = relation?.valueKey ?? 'id';
	const titleField = relation?.titleKey ?? 'title';

	const [isProcess, setIsProcess] = useState<boolean>(false);
	const [options, setOptions] = useState<Array<any>>([]);
	const [page, setPage] = useState(0);
	const [hasMore, setHasMore] = useState(true);
	const [search, setSearch] = useState('');
	// Тут будем хранить массив с созданными вручную записями
	// в массиве потому что, не надо заморачиваться с склеиванием возможно нулевого объекта с массивом
	const [createdOption, setCreatedOptions] = useState<any[]>([]);

	// При изменении строки поиска или страницы, вызываем загрузку данных
	useEffect(() => {
		loadOptions();
	}, [search, page]);

	/** Вызывается при изменении поисковой строки с задержкой */
	const onSearchHandler = (searchText: string) => {
		// При поиске сбрасываем страницу
		if (!!relation.enablePagination && !!searchText && page > 0) {
			setPage(0);
		}
		setSearch(searchText);
	};

	/** Загрузка/подгрузка данных */
	const loadOptions = () => {
		setIsProcess(true);
		// сколько записей пропускать
		const skip = page * pageCount;
		// Вычисляем доп. параметры для запроса
		const additionData = relation.onPrepareRequestData
			? relation.onPrepareRequestData()
			: null;
		API.getOptions(relation, search, page, skip, additionData, pageCount)
			.then((res) => {
				if (res.hasError || !res.result) {
					API.showToast(false, res.errorMessage);
					return;
				}
				const responseOptions = relation.onPrepareResponseData
					? relation.onPrepareResponseData(res.result)
					: res.result;
				// Определяем можно листать дальше
				setHasMore(responseOptions.length >= pageCount);
				// Подготавливаем список элементов
				const list = responseOptions.map(
					(opt: Select2WebElementModel) => {
						const option = opt as unknown as Record<
							string,
							unknown
						>;
						return {
							[valueField]: option[valueField],
							[titleField]: option[titleField],
						};
					},
				);
				setOptions((pr) => {
					// по скипу определяем подгружаем ли мы данные
					if (skip > 0) {
						// склеиваем уже загруженные элементы с свежими
						return [...pr, ...list];
					}
					// или новый поиск — сохраняем выбранные элементы, чтобы лейбл не пропадал
					const selectedIds: string[] = value
						? multiple
							? (value as string[])
							: [value as string]
						: [];
					const missingSelected = pr.filter((o) => {
						return (
							selectedIds.includes(o[valueField]) &&
							!list.some((l) => {
								return l[valueField] === o[valueField];
							})
						);
					});
					return missingSelected.length
						? [...missingSelected, ...list]
						: list;
				});
			})
			.finally(() => {
				return setIsProcess(false);
			});
	};

	/** Вызывается при скролинге списка */
	const handleScroll = (e: any) => {
		// Если мы не в процессе и можем листать страницу
		if (!isProcess && hasMore && !!relation.enablePagination) {
			// определяемся что долистали до низа списка
			if (
				Math.abs(
					e.target.scrollHeight -
						(e.target.scrollTop + e.target.clientHeight),
				) <= 1
			) {
				// увеличиваем страницу
				setPage((prevPage) => {
					return prevPage + 1;
				});
			}
		}
	};

	/** Выбор опции */
	const onChangeSelectedHandler = (selected: Array<any>) => {
		const preparedValues = selected.map((s) => {
			return s[valueField];
		});
		onChangeValue(multiple ? preparedValues : preparedValues[0]);
	};

	/** Создания новой опции */
	const onCreatedHandler = (title: string) => {
		setCreatedOptions(() => {
			onChangeValue(title);
			return [{ [valueField]: title, [titleField]: title }];
		});
	};

	/** Вызывается при открытии/закрытии списка */
	const onChangeOpen = (isOpen: boolean) => {
		setTimeout(() => {
			return setTouched(isOpen, true);
		}, 0);
		// При открытии и наличии функции доп. параметров, то загрузим список...
		if (
			isOpen &&
			(relation.onPrepareRequestData || relation.onPrepareResponseData)
		) {
			loadOptions();
		}
	};

	return (
		<FieldControl
			name={name}
			title={title}
			isRequired={isRequired}
			isInvalid={touched && !!error}
			isTouched={touched}
			errorMsg={error}
		>
			<SelectBox
				id={name}
				autoFocus
				isWait={isProcess}
				allowAdd={allowAdd}
				readonly={disabled}
				placeholder={placeholder}
				isInvalidate={touched && !!error}
				multiple={multiple}
				// склеиваем созданные элемент(ы) с списком
				options={[...createdOption, ...options]}
				valueField={valueField}
				titleField={titleField}
				minSearchLength={3}
				selectedValues={value ? (multiple ? value : [value]) : []}
				onChangeSelected={onChangeSelectedHandler}
				onCreated={onCreatedHandler}
				onSearch={
					relation.serverSideSearch ? onSearchHandler : undefined
				}
				tabIndex={1}
				onScroll={handleScroll}
				onBlur={onBlur}
				onChangeOpen={onChangeOpen}
			/>
			<select
				hidden
				name={name}
				multiple={multiple}
				disabled={true}
				value={value ? (multiple ? value : value) : ''}
			>
				{options.map((opt, i) => {
					return (
						<option
							key={name + '_opt-' + i}
							value={opt[valueField]}
						>
							{opt[titleField]}
						</option>
					);
				})}
			</select>
		</FieldControl>
	);
};

export default RelationSelectBox;
